package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.datetime.LocalDateTime
import nl.volleyplanner.api.GroupType
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.planner.PlanningProblem
import nl.volleyplanner.extensions.now
import nl.volleyplanner.web.ApiDataStateFlow
import nl.volleyplanner.web.BackgroundJob
import nl.volleyplanner.web.BackgroundJobs
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.routing.NavigationManager
import nl.volleyplanner.web.base.routing.PageRouter
import nl.volleyplanner.web.controller.thuisprogramma.assignee_finder.TaskAssigneeStore
import nl.volleyplanner.web.service.OrganisationContext

class ThuisprogrammaProblemData(
    val organisationContext: OrganisationContext,
    val coroutineScope: CoroutineScope,
    val problemSlug: String,
    val thuisprogrammaControlsState: StateFlow<ThuisprogrammaControls>
) {
    private val logger = Logger.getLogger("nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaProblemData")

    val problemDataFlow = ApiDataStateFlow(
        "$problemSlug-problemDataFlow",
        AuthLevel.AdminUser,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope = coroutineScope
    ) {
        organisationContext.volleyplannerApiClient
            .organisation
            .plannerConfiguration
            .getProblemBySlug(problemSlug)!!
    }
    val groupsDataFlow = ApiDataStateFlow(
        "$problemSlug-groupsDataFlow",
        minAccessLevel = AuthLevel.AnonymousPublic,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope
    ) {
       logger.info { "Loading ${problemSlug}-groupsData for ${organisationContext.organisation}" }
        val activeSeason = organisationContext
            .activeSeason
            .filterNotNull()
            .first()

        logger.info { "Got active season: $activeSeason" }
        organisationContext.volleyplannerApiClient
            .organisation
            .groups
            .getGroupsByType(
                activeSeason,
                GroupType.Team
            ).groups
            // TODO: Handle verenigingsleden differently
            .filter { it.name != "Verenigingsleden" }
    }

    val problemVersionsDataFlow = ApiDataStateFlow(
        "$problemSlug-problemVersionsDataFlow",
        AuthLevel.AdminUser,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope = coroutineScope
    ) {
        organisationContext.volleyplannerApiClient
            .organisation
            .plannerConfiguration
            .listProblemVersions(problemSlug)
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    val selectedProblemVersionSlugState = NavigationManager.state
        .distinctUntilChanged { old, new ->
            old.state.queryParameters["versie"] == new.state.queryParameters["versie"]
        }
        .mapLatest {
            it.state.queryParameters["versie"]
        }
        .stateIn(coroutineScope, SharingStarted.Eagerly, null)

    val selectedProblemVersionSlug: String?
        get() = selectedProblemVersionSlugState.value

    private val problemVersionData: MutableMap<String?, ThuisprogrammaVersionData> = mutableMapOf()

    fun getPublishedVersionData() = getVersionData(null)
    fun getSelectedVersionData() = getVersionData(selectedProblemVersionSlugState.value)

    /**
     * @param callerScope The scope from the caller. As this is a StateFlow specifically created for the caller.
     *                      Thus we shouldn't let this StateFlow live longer than the lifetime of the caller
     */
    @Suppress("UNCHECKED_CAST")
    @OptIn(ExperimentalCoroutinesApi::class)
    fun <T> getSelectedVersionPropertyState(callerScope: CoroutineScope, selector: (ThuisprogrammaVersionData) -> StateFlow<T>) : StateFlow<T> {
        return selectedProblemVersionSlugState
            .flatMapLatest { selector(getVersionData(it)) }
            .stateIn(
                callerScope,
                SharingStarted.Eagerly,
                selector(getVersionData(selectedProblemVersionSlugState.value))
            ) as StateFlow<T>
    }

    fun getVersionData(problemVersionSlug: String? = null) : ThuisprogrammaVersionData {
        return problemVersionData.getOrPut(problemVersionSlug) {
            ThuisprogrammaVersionData(organisationContext, coroutineScope, this, problemVersionSlug)
        }
    }

    fun forAllVersions(block: (ThuisprogrammaVersionData) -> Unit) = problemVersionData.values.forEach(block)
}

data class PublishVersionOperation(
    val success: Boolean? = null,
    val exception: Throwable? = null
)

data class MergeChangesBehindPublishedOperation(
    val taskIdsToMerge: Set<String>,
    val completedAt: LocalDateTime? = null,
    val success: Boolean? = null,
    val exception: Throwable? = null
)

// TODO: Also provide with VolleyplannerApiClient?
//  - not truly necessary as versions are not needed for anonymous users
class ThuisprogrammaVersionData(
    val organisationContext: OrganisationContext,
    val coroutineScope: CoroutineScope,
    val problemData: ThuisprogrammaProblemData,
    val problemVersionSlug: String?
) {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.controller.thuisprogramma.ThuisprogrammaVersionData")
    }

    private val problemSlug = problemData.problemSlug

    val taskDaysState = ApiDataStateFlow(
        "$problemSlug-$problemVersionSlug-taskDaysDataFlow",
        minAccessLevel = AuthLevel.AnonymousPublic,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope = coroutineScope
    ) {
        organisationContext.volleyplannerApiClient
            .getThuisProgramma(problemSlug, problemVersionSlug)
    }
    // TODO: change to lazy subscription, ensure taskDaysState is loaded elsewhere first
    //  or find a way to only make an API call to taskDaysState once this stateflow is actively used
    val assigneeStoresState: StateFlow<Map<String, TaskAssigneeStore>?> = taskDaysState.subscribe()
        .map { taskDaysResponse ->
            taskDaysResponse?.taskDays?.map { taskDay ->
                taskDay.matches.map { matchEvent ->
                    matchEvent.allTasks.map { task ->
                        TaskAssigneeStore.getKey(matchEvent.match, task) to TaskAssigneeStore(matchEvent.match, task)
                    }
                }.flatten()
            }?.flatten()
                ?.toMap()
                .also {
                    logger.debug("[$problemVersionSlug] New taskDaysResponse: ${taskDaysResponse?.taskDays?.size} taskDays -> ${it?.size} assignee stores")
                }
        }.stateIn(coroutineScope, SharingStarted.Eagerly, null)

    val thuisprogrammaStatisticsState = ApiDataStateFlow(
        "$problemSlug-$problemVersionSlug-statisticsDataFlow",
        AuthLevel.AdminUser,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope = coroutineScope
    ) {
        organisationContext.volleyplannerApiPlannerClient.getTaskAssignmentStatisticsV2(
            problemSlug,
            problemVersionSlug
        )
    }
    val changesBehindPublishedByTaskIdState = ApiDataStateFlow(
        "$problemSlug-$problemVersionSlug-changesBehindDataFlow",
        AuthLevel.AdminUser,
        contextFlow = MutableStateFlow(Unit),
        coroutineScope = coroutineScope
    ) {
        problemVersionSlug?.let {
            organisationContext.volleyplannerApiClient
                .organisation
                .plannerConfiguration
                .getChangesBehindPublished(problemSlug, problemVersionSlug)
                .associateBy { it.id }
        } ?: emptyMap()
    }

    val changesBehindPublishedOperationJob: StateFlow<BackgroundJob<MergeChangesBehindPublishedOperation>?>
        get() = BackgroundJobs.getJob("merge-changes-behind-$problemSlug-$problemVersionSlug")

    val publishOperationJob: StateFlow<BackgroundJob<PublishVersionOperation>?>
        get() = BackgroundJobs.getJob<PublishVersionOperation>("publish-version-$problemSlug-$problemVersionSlug")

    val canPublishVersion = {
        problemData: PlanningProblem?,
        changesJob: BackgroundJob<MergeChangesBehindPublishedOperation>?,
        publishJob: BackgroundJob<PublishVersionOperation>? ->
        if (problemData == null) {
            false
        } else {
            changesJob?.isRunning != true && publishJob?.isRunning != true
                    && !problemData.published //&& problemData.endDate > LocalDate.today()
        }
    }.let { canPublishFn ->
        // Avoiding API calls requiring additional permissions for non admin users
        // TODO: Need to find a cleaner way in Thuisprogramma to achieve this, maybe hard split anonymous vs admin?
        if (AuthContextService.authUser!!.authLevel <= AuthLevel.BasicUser) {
            flow { emit(false) }
                .stateIn(coroutineScope, SharingStarted.Eagerly, false)
        } else {
            combine(problemData.problemDataFlow.subscribe(), changesBehindPublishedOperationJob, publishOperationJob) { problemData, changesJob, publishJob ->
                canPublishFn(problemData, changesJob, publishJob)
            }.stateIn(coroutineScope, SharingStarted.Eagerly, canPublishFn(
                problemData.problemDataFlow.value, changesBehindPublishedOperationJob.value, publishOperationJob.value
            ))
        }
    }

    fun publishVersion() {
        require(problemVersionSlug != null) { "Can only merge non-published versions" }
        require(publishOperationJob.value == null || !publishOperationJob.value!!.job.isActive) {
            "Cannot publish if another publish is still in progress"
        }

        BackgroundJobs.launchJob<PublishVersionOperation>("publish-version-$problemSlug-$problemVersionSlug") {
            val result = runCatching {
                organisationContext.volleyplannerApiClient
                    .organisation
                    .plannerConfiguration
                    .publishProblemVersion(problemSlug, problemVersionSlug!!)
            }
            delay(500L)
            if(result.isSuccess) {
                problemData.getPublishedVersionData().let {
                    it.taskDaysState.refresh()
                    it.thuisprogrammaStatisticsState.refresh()
                }
                problemData.forAllVersions {
                    it.changesBehindPublishedByTaskIdState.refresh()
                }

                // Switch to the published version
                NavigationManager.setQueryParameters(mapOf("versie" to null))
            }
            PublishVersionOperation(success = result.isSuccess, exception = result.exceptionOrNull())
        }
    }

    fun mergeChangesBehindPublished(taskIdsToMerge: Set<String>) {
        require(problemVersionSlug != null) { "Cannot merge changes behind published when no version is selected" }
        require(changesBehindPublishedOperationJob.value == null || !changesBehindPublishedOperationJob.value!!.job.isActive) {
            "Cannot merge changes while another merge is still in progress"
        }

        val jobState = MutableStateFlow<MergeChangesBehindPublishedOperation?>(MergeChangesBehindPublishedOperation(taskIdsToMerge))
        val mergeJob = BackgroundJobs.launchJob<MergeChangesBehindPublishedOperation>("") {
            val result = runCatching {
                organisationContext.volleyplannerApiClient
                    .organisation
                    .plannerConfiguration
                    .mergeChangesBehindPublished(problemSlug, problemVersionSlug!!, taskIdsToMerge)
            }
            delay(500L)
            jobState.value = jobState.value!!.copy(
                completedAt = LocalDateTime.now(),
                success = result.isSuccess,
                exception = result.exceptionOrNull()
            )

            // Refresh the changes behind data, so the component can re-evaluate what to display
            if (result.isSuccess) {
                taskDaysState.refresh()
                changesBehindPublishedByTaskIdState.refresh()
            }
            jobState.value!!
        }
    }
}

class ThuisprogrammaDataManager(
    val problemSlug: String,
    val thuisprogrammaControlsState: MutableStateFlow<ThuisprogrammaControls>,
    val defaultDateFilter: String,
    val organisationContext: OrganisationContext,
    val coroutineScope: CoroutineScope
) {
    val problemData = ThuisprogrammaProblemData(organisationContext, coroutineScope, problemSlug, thuisprogrammaControlsState)
    val thuisprogrammaFiltersState = PageRouter.currentPageRouteContext
        .map { pageRouteContext ->
            val taskDayParam = pageRouteContext?.pathParameters["taskDay"] ?: pageRouteContext?.urlState?.queryParameters["datum"]
            val teamFilterParam = pageRouteContext?.urlState?.queryParameters["team"]

            ThuisprogrammaFilters(taskDayParam, teamFilterParam, defaultDateFilter)
        }.stateIn(coroutineScope, SharingStarted.Eagerly, ThuisprogrammaFilters(null, null))
}